import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, QrCode, Download, Music, Disc, Video, Calendar,
  RefreshCw, ExternalLink, Copy, Check, Printer, FileDown, Grid
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminQRCodes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tracks');
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [qrCodes, setQrCodes] = useState({});
  const [baseUrl, setBaseUrl] = useState('https://casasamba.com');
  const [copiedId, setCopiedId] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [selectedForPrint, setSelectedForPrint] = useState([]);
  const printRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_auth');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Basic ${localStorage.getItem('admin_token') || sessionStorage.getItem('admin_auth')}` }
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch data and pre-stored QR codes
      const [tracksRes, albumsRes, coursesRes, classesRes, trackQRsRes, albumQRsRes] = await Promise.all([
        axios.get(`${API}/api/tracks`),
        axios.get(`${API}/api/albums`),
        axios.get(`${API}/api/video-courses`),
        axios.get(`${API}/api/classes`),
        axios.get(`${API}/api/quick-buy/qr-codes/tracks`).catch(() => ({ data: { tracks: [] } })),
        axios.get(`${API}/api/quick-buy/qr-codes/albums`).catch(() => ({ data: { albums: [] } }))
      ]);
      
      setTracks(tracksRes.data);
      setAlbums(albumsRes.data);
      setCourses(coursesRes.data);
      setClasses(classesRes.data);
      
      // Pre-populate QR codes from stored data
      const storedQRs = {};
      trackQRsRes.data.tracks?.forEach(qr => {
        storedQRs[`track-${qr.id}`] = qr;
      });
      albumQRsRes.data.albums?.forEach(qr => {
        storedQRs[`album-${qr.id}`] = qr;
      });
      setQrCodes(storedQRs);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (type, id) => {
    const key = `${type}-${id}`;
    if (qrCodes[key]) return; // Already generated
    
    try {
      let endpoint = '';
      switch (type) {
        case 'track':
          // Use quick-buy endpoint for instant purchase QR codes
          endpoint = `/api/quick-buy/qr/track/${id}/base64`;
          break;
        case 'album':
          endpoint = `/api/quick-buy/qr/album/${id}/base64`;
          break;
        case 'course':
          endpoint = `/api/qr/video-course/${id}/base64?base_url=${encodeURIComponent(baseUrl)}`;
          break;
        case 'class':
          endpoint = `/api/qr/class/${id}/base64?base_url=${encodeURIComponent(baseUrl)}`;
          break;
        default:
          return;
      }
      
      const response = await axios.get(`${API}${endpoint}`);
      setQrCodes(prev => ({ ...prev, [key]: response.data }));
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const generateAllQRCodes = async () => {
    const items = activeTab === 'tracks' ? tracks : 
                  activeTab === 'albums' ? albums : 
                  activeTab === 'courses' ? courses : classes;
    const type = activeTab === 'tracks' ? 'track' : 
                 activeTab === 'albums' ? 'album' : 
                 activeTab === 'courses' ? 'course' : 'class';
    
    toast.info(`Generating ${items.length} QR codes...`);
    
    for (const item of items) {
      await generateQRCode(type, item.id);
    }
    
    toast.success('All QR codes generated!');
  };

  const downloadQRCode = (qrData, title) => {
    const link = document.createElement('a');
    link.href = qrData.qr_code;
    link.download = `qr-${title.replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('URL copied!');
  };

  // Print single QR code
  const printSingleQR = (qrData, title, price) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${title}</title>
        <style>
          body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
          .qr-card { display: inline-block; border: 2px solid #333; padding: 20px; border-radius: 12px; }
          .qr-code { width: 200px; height: 200px; }
          h2 { margin: 15px 0 5px; font-size: 18px; }
          .price { font-size: 24px; font-weight: bold; color: #f59e0b; margin: 10px 0; }
          .scan-text { font-size: 12px; color: #666; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="qr-card">
          <img loading="lazy" decoding="async" src="${qrData.qr_code}" class="qr-code" />
          <h2>${title}</h2>
          <div class="price">$${price?.toFixed(2) || '0.00'}</div>
          <p class="scan-text">Scan to buy & download instantly</p>
        </div>
        <script>window.onload = () => { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Toggle selection for bulk print
  const togglePrintSelection = (key) => {
    setSelectedForPrint(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  // Select all for print
  const selectAllForPrint = () => {
    const items = activeTab === 'tracks' ? tracks : 
                  activeTab === 'albums' ? albums : 
                  activeTab === 'courses' ? courses : classes;
    const type = activeTab === 'tracks' ? 'track' : 
                 activeTab === 'albums' ? 'album' : 
                 activeTab === 'courses' ? 'course' : 'class';
    
    const allKeys = items.map(item => `${type}-${item.id}`).filter(key => qrCodes[key]);
    setSelectedForPrint(allKeys);
  };

  // Print multiple QR codes
  const printSelectedQRs = () => {
    if (selectedForPrint.length === 0) {
      toast.error('Select QR codes to print first');
      return;
    }

    const items = activeTab === 'tracks' ? tracks : 
                  activeTab === 'albums' ? albums : 
                  activeTab === 'courses' ? courses : classes;

    const printContent = selectedForPrint.map(key => {
      const qrData = qrCodes[key];
      const [type, id] = key.split('-');
      const item = items.find(i => i.id === id || key === `${type}-${i.id}`);
      if (!qrData || !item) return '';
      
      return `
        <div class="qr-card">
          <img loading="lazy" decoding="async" src="${qrData.qr_code}" class="qr-code" />
          <h3>${item.title || item.name}</h3>
          <div class="price">$${(item.price || qrData.price || 0).toFixed(2)}</div>
          <p class="scan-text">Scan to buy</p>
        </div>
      `;
    }).join('');

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Codes - Escola de Samba Casa Samba</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
          .qr-card { border: 1px solid #ccc; padding: 15px; border-radius: 8px; text-align: center; break-inside: avoid; }
          .qr-code { width: 150px; height: 150px; }
          h3 { margin: 10px 0 5px; font-size: 14px; }
          .price { font-size: 18px; font-weight: bold; color: #f59e0b; }
          .scan-text { font-size: 10px; color: #666; }
          @media print { 
            .grid { grid-template-columns: repeat(3, 1fr); }
            .qr-card { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1 style="text-align: center; margin-bottom: 30px;">Escola de Samba Casa Samba - QR Codes</h1>
        <div class="grid">${printContent}</div>
        <script>window.onload = () => { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Export all as downloadable ZIP (individual PNGs)
  const exportAllAsImages = async () => {
    const items = activeTab === 'tracks' ? tracks : 
                  activeTab === 'albums' ? albums : 
                  activeTab === 'courses' ? courses : classes;
    const type = activeTab === 'tracks' ? 'track' : 
                 activeTab === 'albums' ? 'album' : 
                 activeTab === 'courses' ? 'course' : 'class';

    toast.info('Generating QR codes for download...');

    // Generate all QR codes first
    for (const item of items) {
      const key = `${type}-${item.id}`;
      if (!qrCodes[key]) {
        await generateQRCode(type, item.id);
      }
    }

    // Download each one
    let count = 0;
    for (const item of items) {
      const key = `${type}-${item.id}`;
      const qrData = qrCodes[key];
      if (qrData) {
        const link = document.createElement('a');
        link.href = qrData.qr_code;
        link.download = `qr-${(item.title || item.name).replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        count++;
        // Small delay between downloads
        await new Promise(r => setTimeout(r, 100));
      }
    }

    toast.success(`Downloaded ${count} QR codes!`);
  };

  const renderItems = () => {
    const items = activeTab === 'tracks' ? tracks : 
                  activeTab === 'albums' ? albums : 
                  activeTab === 'courses' ? courses : classes;
    const type = activeTab === 'tracks' ? 'track' : 
                 activeTab === 'albums' ? 'album' : 
                 activeTab === 'courses' ? 'course' : 'class';
    
    if (items.length === 0) {
      return (
        <div className="text-center py-12 text-stone-500">
          <QrCode className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No {activeTab} found</p>
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(item => {
          const key = `${type}-${item.id}`;
          const qrData = qrCodes[key];
          const title = item.title || item.name;
          const isSelected = selectedForPrint.includes(key);
          
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-stone-900 border rounded-xl p-4 ${isSelected ? 'border-amber-500' : 'border-stone-800'}`}
            >
              <div className="flex items-start gap-4">
                {/* Checkbox for print selection */}
                {qrData && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => togglePrintSelection(key)}
                    className="mt-1 w-4 h-4 rounded border-stone-600 text-amber-500 focus:ring-amber-500"
                  />
                )}
                
                {/* QR Code */}
                <div className="flex-shrink-0">
                  {qrData ? (
                    <img loading="lazy" decoding="async" 
                      src={qrData.qr_code} 
                      alt={`QR for ${title}`}
                      className="w-24 h-24 rounded-lg bg-white p-1"
                    />
                  ) : (
                    <button
                      onClick={() => generateQRCode(type, item.id)}
                      className="w-24 h-24 rounded-lg bg-stone-800 border border-stone-700 flex items-center justify-center hover:bg-stone-700 transition-colors"
                    >
                      <QrCode className="w-8 h-8 text-stone-500" />
                    </button>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate">{title}</h4>
                  <p className="text-sm text-stone-400 capitalize">{type}</p>
                  {qrData?.price && (
                    <p className="text-amber-500 font-bold">${qrData.price.toFixed(2)}</p>
                  )}
                  
                  {qrData && (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center gap-1 text-xs text-stone-500">
                        <span className="truncate max-w-[120px]">{qrData.url}</span>
                        <button
                          onClick={() => copyUrl(qrData.url, item.id)}
                          className="p-1 hover:bg-stone-800 rounded"
                        >
                          {copiedId === item.id ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => downloadQRCode(qrData, title)}
                          className="flex items-center gap-1 px-2 py-1 bg-amber-500 text-stone-900 rounded text-xs font-medium hover:bg-amber-400"
                          title="Download PNG"
                        >
                          <Download className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => printSingleQR(qrData, title, item.price || qrData.price)}
                          className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded text-xs font-medium hover:bg-blue-400"
                          title="Print QR Code"
                        >
                          <Printer className="w-3 h-3" />
                        </button>
                        <a
                          href={qrData.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2 py-1 bg-stone-700 text-white rounded text-xs font-medium hover:bg-stone-600"
                          title="Open Link"
                        >
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      {/* Header */}
      <div className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-amber-500" />
                  QR Code Generator
                </h1>
                <p className="text-sm text-stone-400">Generate QR codes for products and classes</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={generateAllQRCodes}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-stone-900 rounded-lg font-bold hover:bg-amber-400 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Generate All
              </button>
              {selectedForPrint.length > 0 && (
                <>
                  <button
                    onClick={printSelectedQRs}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-400 transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                    Print ({selectedForPrint.length})
                  </button>
                  <button
                    onClick={() => setSelectedForPrint([])}
                    className="px-3 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 text-sm"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Bulk Actions Bar */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={selectAllForPrint}
              className="flex items-center gap-2 px-3 py-2 bg-stone-800 rounded-lg hover:bg-stone-700 text-sm"
            >
              <Grid className="w-4 h-4" />
              Select All Generated
            </button>
            {selectedForPrint.length > 0 && (
              <span className="text-sm text-stone-400">
                {selectedForPrint.length} selected
              </span>
            )}
          </div>
          <button
            onClick={exportAllAsImages}
            className="flex items-center gap-2 px-3 py-2 bg-stone-800 rounded-lg hover:bg-stone-700 text-sm"
          >
            <FileDown className="w-4 h-4" />
            Download All PNGs
          </button>
        </div>

        {/* Base URL Setting */}
        <div className="mb-6 bg-stone-900 border border-stone-800 rounded-xl p-4">
          <label className="block text-sm font-medium mb-2">Base URL for QR Codes</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://yourdomain.com"
              className="flex-1 px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg focus:ring-2 focus:ring-amber-500"
            />
            <button
              onClick={() => { setQrCodes({}); toast.info('QR codes cleared. Click items to regenerate.'); }}
              className="px-4 py-2 bg-stone-700 rounded-lg hover:bg-stone-600"
            >
              Clear Cache
            </button>
          </div>
          <p className="text-xs text-stone-500 mt-2">
            QR codes now link directly to instant purchase pages. Customers scan → pay → download instantly.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'tracks', label: 'Tracks', icon: Music, count: tracks.length },
            { id: 'albums', label: 'Albums', icon: Disc, count: albums.length },
            { id: 'courses', label: 'Video Courses', icon: Video, count: courses.length },
            { id: 'classes', label: 'Classes', icon: Calendar, count: classes.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-amber-500 text-stone-900'
                  : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Items */}
        {renderItems()}

        {/* Instructions */}
        <div className="mt-8 bg-stone-900 border border-stone-800 rounded-xl p-6">
          <h3 className="font-bold mb-3">How to Use QR Codes</h3>
          <ul className="space-y-2 text-sm text-stone-400">
            <li>• <strong>Print on physical media:</strong> Add QR codes to CD cases, flyers, or merchandise</li>
            <li>• <strong>Class registration:</strong> Display QR codes at your studio for easy sign-ups</li>
            <li>• <strong>Marketing materials:</strong> Include in posters, business cards, and advertisements</li>
            <li>• <strong>Product packaging:</strong> Link physical products to their digital versions</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminQRCodes;
